import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Trash2, Check, X } from "lucide-react";
import MainLayout from "@/components/layout/MainLayout";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from "@/components/ui/form";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { fetchTemplateAgents, clearTemplateCache, getTemplateById, updateTemplate } from "@/services/templateService";
import { Tables } from "@/types/database.types";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

type TemplateAgent = Tables<"agents"> & {
    industry_id?: string | null;
    persona_id?: string | null;
    hidden_template_instructions?: string | null;
    avatar_url?: string | null;
    welcome_message?: string | null;
    thank_you_message?: string | null;
    agent_name?: string | null;
    organization?: { name?: string };
    industry?: { name?: string; emoji?: string };
    persona?: { avatar_name?: string; type?: string };
    organizations?: { name?: string };
};

// Form validation schema
const templateFormSchema = z.object({
    title: z.string().min(1, "Title is required"),
    industry_id: z.string().optional(),
    persona_id: z.string().optional(),
    instructions: z.string().optional(),
    hidden_template_instructions: z.string().optional(),
    avatar_url: z.string().optional(),
    is_active: z.boolean().default(true),
    welcome_message: z.string().optional(),
    thank_you_message: z.string().optional(),
    agent_name: z.string().optional(),
});

const TemplateDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { toast } = useToast();
    const [template, setTemplate] = useState<TemplateAgent | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [industries, setIndustries] = useState<Tables<"industries">[]>([]);
    const [personas, setPersonas] = useState<Tables<"personas">[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Set up form with default values
    const form = useForm<z.infer<typeof templateFormSchema>>({
        resolver: zodResolver(templateFormSchema),
        defaultValues: {
            title: "",
            industry_id: "",
            persona_id: "",
            instructions: "",
            hidden_template_instructions: "",
            avatar_url: "",
            is_active: true,
            welcome_message: "",
            thank_you_message: "",
            agent_name: "",
        }
    });

    const fetchTemplate = useCallback(async () => {
        if (!id) return;

        setIsLoading(true);
        try {
            const { data, error } = await supabase
                .from("agents")
                .select(`
                    *,
                    organization:organizations(name),
                    industry:industries(name, emoji),
                    persona:personas(avatar_name, type)
                `)
                .eq("id", id)
                .single();

            if (error) throw error;

            setTemplate(data);
        } catch (error) {
            console.error("Error fetching template:", error);
            toast({
                variant: "destructive",
                title: "Error",
                description: "Failed to load template",
            });
        } finally {
            setIsLoading(false);
        }
    }, [id, toast]);

    const fetchIndustries = useCallback(async () => {
        try {
            const { data, error } = await supabase
                .from("industries")
                .select("*")
                .order("name");

            if (error) throw error;
            setIndustries(data || []);
        } catch (error) {
            console.error("Error fetching industries:", error);
        }
    }, []);

    const fetchPersonas = useCallback(async () => {
        try {
            const { data, error } = await supabase
                .from("personas")
                .select("*")
                .order("avatar_name");

            if (error) throw error;
            setPersonas(data || []);
        } catch (error) {
            console.error("Error fetching personas:", error);
        }
    }, []);

    useEffect(() => {
        fetchTemplate();
        fetchIndustries();
        fetchPersonas();
    }, [fetchTemplate, fetchIndustries, fetchPersonas]);

    // Populate form with template data when template loads
    useEffect(() => {
        if (template) {
            form.reset({
                title: template.title || "",
                industry_id: template.industry_id || "",
                persona_id: template.persona_id || "",
                instructions: template.instructions || "",
                hidden_template_instructions: template.hidden_template_instructions || "",
                avatar_url: template.avatar_url || "",
                is_active: template.is_active ?? true,
                welcome_message: template.welcome_message || "",
                thank_you_message: template.thank_you_message || "",
                agent_name: template.agent_name || "",
            });
        }
    }, [template, form]);

    const onSubmit = async (values: z.infer<typeof templateFormSchema>) => {
        if (!template) return;

        setIsSaving(true);
        try {
            const { data, error } = await supabase
                .from("agents")
                .update({
                    title: values.title,
                    instructions: values.instructions,
                    industry_id: values.industry_id || null,
                    persona_id: values.persona_id || null,
                    hidden_template_instructions: values.hidden_template_instructions || null,
                    avatar_url: values.avatar_url || null,
                    is_active: values.is_active,
                    welcome_message: values.welcome_message || null,
                    thank_you_message: values.thank_you_message || null,
                    agent_name: values.agent_name || null,
                    updated_at: new Date().toISOString(),
                })
                .eq("id", template.id)
                .select()
                .single();

            if (error) throw error;

            setTemplate(data);
            toast({
                title: "Success",
                description: "Template updated successfully",
            });
        } catch (error) {
            console.error("Error updating template:", error);
            toast({
                variant: "destructive",
                title: "Error",
                description: "Failed to update template",
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!template) return;

        // For now, just show a toast message
        toast({
            variant: "destructive",
            title: "Delete template",
            description: "Template deletion functionality is not implemented yet"
        });
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };

    if (isLoading) {
        return (
            <MainLayout>
                <SuperAdminLayout
                    title="Template Details"
                    description="Loading template information..."
                >
                    <div className="flex items-center justify-center py-12">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                    </div>
                </SuperAdminLayout>
            </MainLayout>
        );
    }

    if (!template) {
        return (
            <MainLayout>
                <SuperAdminLayout
                    title="Template Not Found"
                    description="The requested template could not be found"
                >
                    <Button onClick={() => navigate("/super-admin/templates")}>
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to Templates
                    </Button>
                </SuperAdminLayout>
            </MainLayout>
        );
    }

    const actions = (
        <div className="flex gap-2">
            {isEditing ? (
                <>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditing(false)}
                    >
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                    </Button>
                    <Button
                        size="sm"
                        onClick={form.handleSubmit(onSubmit)}
                    >
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                    </Button>
                </>
            ) : (
                <>
                    <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleDelete}
                    >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => setIsEditing(true)}
                    >
                        Edit Template
                    </Button>
                </>
            )}
        </div>
    );

    return (
        <MainLayout>
            <SuperAdminLayout
                title={template.title}
                description={`Template ID: ${template.id}`}
                actions={actions}
            >
                <div className="mb-6">
                    <Button
                        variant="outline"
                        onClick={() => navigate("/super-admin/templates")}
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to Templates
                    </Button>
                </div>

                <Tabs defaultValue="details">
                    <TabsList>
                        <TabsTrigger value="details">Details</TabsTrigger>
                        <TabsTrigger value="configuration">Configuration</TabsTrigger>
                        <TabsTrigger value="messages">Messages</TabsTrigger>
                    </TabsList>

                    <TabsContent value="details" className="mt-6">
                        {isEditing ? (
                            <Form {...form}>
                                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                                    <Card>
                                        <CardHeader>
                                            <CardTitle>Template Details</CardTitle>
                                            <CardDescription>Basic information about this template</CardDescription>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            <FormField
                                                control={form.control}
                                                name="title"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Title</FormLabel>
                                                        <FormControl>
                                                            <Input placeholder="Template title" {...field} />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={form.control}
                                                name="industry_id"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Industry</FormLabel>
                                                        <Select
                                                            onValueChange={field.onChange}
                                                            defaultValue={field.value}
                                                        >
                                                            <FormControl>
                                                                <SelectTrigger>
                                                                    <SelectValue placeholder="Select an industry" />
                                                                </SelectTrigger>
                                                            </FormControl>
                                                            <SelectContent>
                                                                {industries.map(industry => (
                                                                    <SelectItem key={industry.id} value={industry.id}>
                                                                        {industry.name}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={form.control}
                                                name="persona_id"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Persona</FormLabel>
                                                        <Select
                                                            onValueChange={field.onChange}
                                                            defaultValue={field.value}
                                                        >
                                                            <FormControl>
                                                                <SelectTrigger>
                                                                    <SelectValue placeholder="Select a persona" />
                                                                </SelectTrigger>
                                                            </FormControl>
                                                            <SelectContent>
                                                                {personas.map(persona => (
                                                                    <SelectItem key={persona.id} value={persona.id}>
                                                                        {persona.avatar_name}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={form.control}
                                                name="is_active"
                                                render={({ field }) => (
                                                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                                        <div className="space-y-0.5">
                                                            <FormLabel className="text-base">
                                                                Active Status
                                                            </FormLabel>
                                                            <FormDescription>
                                                                Whether this template is available for use
                                                            </FormDescription>
                                                        </div>
                                                        <FormControl>
                                                            <Switch
                                                                checked={field.value}
                                                                onCheckedChange={field.onChange}
                                                            />
                                                        </FormControl>
                                                    </FormItem>
                                                )}
                                            />
                                        </CardContent>
                                    </Card>
                                </form>
                            </Form>
                        ) : (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Template Details</CardTitle>
                                    <CardDescription>Basic information about this template</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div>
                                        <h3 className="text-sm font-medium text-gray-500">Status</h3>
                                        <div className="mt-1">
                                            {template.is_active ? (
                                                <Badge variant="outline" className="bg-green-100 text-green-800">
                                                    <Check className="h-3 w-3 mr-1" /> Active
                                                </Badge>
                                            ) : (
                                                <Badge variant="secondary" className="bg-gray-100 text-gray-800">
                                                    <X className="h-3 w-3 mr-1" /> Inactive
                                                </Badge>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="text-sm font-medium text-gray-500">Industry</h3>
                                        <p className="mt-1 text-sm">{template.industry_id ? industries.find(i => i.id === template.industry_id)?.name : "No Industry"}</p>
                                    </div>

                                    <div>
                                        <h3 className="text-sm font-medium text-gray-500">Persona</h3>
                                        <p className="mt-1 text-sm">{template.persona_id ? personas.find(p => p.id === template.persona_id)?.avatar_name : "No Persona"}</p>
                                    </div>

                                    <div>
                                        <h3 className="text-sm font-medium text-gray-500">Created</h3>
                                        <p className="mt-1 text-sm">{formatDate(template.created_at)}</p>
                                    </div>

                                    <div>
                                        <h3 className="text-sm font-medium text-gray-500">Last Updated</h3>
                                        <p className="mt-1 text-sm">{formatDate(template.updated_at)}</p>
                                    </div>

                                    <div>
                                        <h3 className="text-sm font-medium text-gray-500">Template ID</h3>
                                        <p className="mt-1 text-sm font-mono text-xs">{template.id}</p>
                                    </div>

                                    <div>
                                        <h3 className="text-sm font-medium text-gray-500">Created By</h3>
                                        <p className="mt-1 text-sm font-mono text-xs">{template.created_by}</p>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>

                    <TabsContent value="configuration" className="mt-6">
                        {isEditing ? (
                            <Form {...form}>
                                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                                    <Card>
                                        <CardHeader>
                                            <CardTitle>Template Configuration</CardTitle>
                                            <CardDescription>Advanced settings for this template</CardDescription>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            <FormField
                                                control={form.control}
                                                name="agent_name"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Agent Name</FormLabel>
                                                        <FormControl>
                                                            <Input placeholder="Display name for this agent" {...field} value={field.value || ""} />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={form.control}
                                                name="avatar_url"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Avatar Icon</FormLabel>
                                                        <FormControl>
                                                            <Input placeholder="Icon name (e.g., Users, BarChart)" {...field} value={field.value || ""} />
                                                        </FormControl>
                                                        <FormDescription>
                                                            Use Lucide icon names (Users, BarChart, Shield, etc.)
                                                        </FormDescription>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={form.control}
                                                name="hidden_template_instructions"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Hidden Template Instructions</FormLabel>
                                                        <FormControl>
                                                            <Textarea
                                                                placeholder="Internal instructions for template usage (not visible to end users)"
                                                                className="resize-none"
                                                                rows={4}
                                                                {...field}
                                                                value={field.value || ""}
                                                            />
                                                        </FormControl>
                                                        <FormDescription>
                                                            These instructions are only visible to administrators and help with template management.
                                                        </FormDescription>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </CardContent>
                                    </Card>
                                </form>
                            </Form>
                        ) : (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Template Configuration</CardTitle>
                                    <CardDescription>Advanced settings for this template</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div>
                                        <h3 className="text-sm font-medium text-gray-500">Agent Name</h3>
                                        <p className="mt-1 text-sm">{template.agent_name || "Not specified"}</p>
                                    </div>

                                    <div>
                                        <h3 className="text-sm font-medium text-gray-500">Avatar Icon</h3>
                                        <p className="mt-1 text-sm">{template.avatar_url || "Default"}</p>
                                    </div>

                                    <div>
                                        <h3 className="text-sm font-medium text-gray-500">Hidden Template Instructions</h3>
                                        <p className="mt-1 text-sm">{template.hidden_template_instructions || "No special instructions"}</p>
                                        <p className="mt-1 text-xs text-gray-400">Admin-only field for template management</p>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>

                    <TabsContent value="messages" className="mt-6">
                        {isEditing ? (
                            <Form {...form}>
                                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                                    <Card>
                                        <CardHeader>
                                            <CardTitle>Message Templates</CardTitle>
                                            <CardDescription>Customize messages shown to users</CardDescription>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            <FormField
                                                control={form.control}
                                                name="welcome_message"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Welcome Message</FormLabel>
                                                        <FormControl>
                                                            <Textarea
                                                                placeholder="Message shown when user starts a conversation"
                                                                className="resize-none"
                                                                rows={3}
                                                                {...field}
                                                                value={field.value || ""}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={form.control}
                                                name="thank_you_message"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Thank You Message</FormLabel>
                                                        <FormControl>
                                                            <Textarea
                                                                placeholder="Message shown at the end of a conversation"
                                                                className="resize-none"
                                                                rows={3}
                                                                {...field}
                                                                value={field.value || ""}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={form.control}
                                                name="instructions"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Agent Instructions</FormLabel>
                                                        <FormControl>
                                                            <Textarea
                                                                placeholder="Instructions for the AI agent"
                                                                className="resize-none"
                                                                rows={6}
                                                                {...field}
                                                                value={field.value || ""}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </CardContent>
                                    </Card>
                                </form>
                            </Form>
                        ) : (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Message Templates</CardTitle>
                                    <CardDescription>Customize messages shown to users</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div>
                                        <h3 className="text-sm font-medium text-gray-500">Welcome Message</h3>
                                        <p className="mt-1 text-sm p-3 bg-gray-50 rounded-md">
                                            {template.welcome_message || "No welcome message configured"}
                                        </p>
                                    </div>

                                    <div>
                                        <h3 className="text-sm font-medium text-gray-500">Thank You Message</h3>
                                        <p className="mt-1 text-sm p-3 bg-gray-50 rounded-md">
                                            {template.thank_you_message || "No thank you message configured"}
                                        </p>
                                    </div>

                                    <div>
                                        <h3 className="text-sm font-medium text-gray-500">Agent Instructions</h3>
                                        <p className="mt-1 text-sm p-3 bg-gray-50 rounded-md whitespace-pre-wrap">
                                            {template.instructions || "No instructions configured"}
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>
                </Tabs>
            </SuperAdminLayout>
        </MainLayout>
    );
};

export default TemplateDetailPage; 